import React, { useState, useEffect } from 'react';
import { apiRequest } from '@/utils/api-request';
import { downloadCsvExport } from '@/utils/download-csv';
import { Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Star, Calendar, Download, CheckCircle, XCircle } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';

interface ReviewData {
    reviewId: number;
    vertical_id_fk: number;
    r_name: string;
    r_phone: string;
    r_email: string;
    rating: number;
    review_text: string;
    review_date: string;
    owner_response_text: string | null;
    review_reply: string | null;
    review_status: number;
    recommend_this_school: number;
    relationship_with_school: string | null;
    school_name: string;
    city_name: string;
    area_name: string;
    claimed: number;
    is_client: number;
}

interface Pagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

const ReviewsAdmin: React.FC = () => {
    const [reviews, setReviews] = useState<ReviewData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState<Pagination>({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 25,
    });
    const [sortColumn, setSortColumn] = useState('review_date');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    const [claimedFilter, setClaimedFilter] = useState<string>('');
    const [clientFilter, setClientFilter] = useState<string>('');

    // Modal state
    const [selectedReview, setSelectedReview] = useState<ReviewData | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);

    // Fetch reviews
    const fetchReviews = async (page: number = pagination.currentPage) => {
        try {
            setIsLoading(true);
            const response = await apiRequest('/api/reviews', {
                method: 'POST',
                body: JSON.stringify({
                    page,
                    limit: pagination.itemsPerPage,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortDirection,
                    from_date: appliedFromDate || null,
                    to_date: appliedToDate || null,
                    claimed_filter: claimedFilter !== '' ? parseInt(claimedFilter) : null,
                    client_filter: clientFilter !== '' ? parseInt(clientFilter) : null,
                }),
            });

            if (response.success) {
                setReviews(response.data.reviews || []);
                setPagination(response.data.pagination);
            } else {
                showToast('Failed to fetch reviews', 'error');
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
            showToast('Failed to fetch reviews', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Load reviews on mount and when filters change
    useEffect(() => {
        fetchReviews(1);
    }, [searchTerm, sortColumn, sortDirection, appliedFromDate, appliedToDate, claimedFilter, clientFilter]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchReviews(newPage);
        }
    };

    // Handle sort
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
    };

    // Handle date filter apply
    const handleApplyFilters = () => {
        setAppliedFromDate(fromDate);
        setAppliedToDate(toDate);
    };

    // Handle date filter clear
    const handleClearFilters = () => {
        setFromDate('');
        setToDate('');
        setAppliedFromDate('');
        setAppliedToDate('');
        setClaimedFilter('');
        setClientFilter('');
    };

    // Handle exporting the currently filtered reviews as CSV
    const handleExport = async () => {
        try {
            setIsExporting(true);
            await downloadCsvExport('/api/reviews/export', {
                search_term: searchTerm,
                from_date: appliedFromDate || null,
                to_date: appliedToDate || null,
                claimed_filter: claimedFilter !== '' ? claimedFilter : null,
                client_filter: clientFilter !== '' ? clientFilter : null,
            });
        } catch (error) {
            console.error('Error exporting reviews:', error);
            showToast('Failed to export reviews', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    // Handle review click
    const handleReviewClick = (review: ReviewData) => {
        setSelectedReview(review);
        setReplyText(review.review_reply || review.owner_response_text || '');
        setIsModalOpen(true);
    };

    // Handle modal close
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedReview(null);
        setReplyText('');
    };

    // Handle reply submission
    const handleSubmitReply = async () => {
        if (!selectedReview || !replyText.trim()) {
            showToast('Please enter a reply', 'error');
            return;
        }

        try {
            setIsSubmittingReply(true);
            const response = await apiRequest(`/api/reviews/${selectedReview.reviewId}/reply`, {
                method: 'PUT',
                body: JSON.stringify({
                    reply_text: replyText,
                }),
            });

            if (response.success) {
                showToast('Reply submitted successfully', 'success');
                handleCloseModal();
                // Refresh reviews to show updated reply
                fetchReviews(pagination.currentPage);
            } else {
                showToast('Failed to submit reply', 'error');
            }
        } catch (error) {
            console.error('Error submitting reply:', error);
            showToast('Failed to submit reply', 'error');
        } finally {
            setIsSubmittingReply(false);
        }
    };

    // Truncate review text for table display
    const truncateText = (text: string, maxLength: number = 80) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Format date and time
    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        }).replace(/am/i, 'AM').replace(/pm/i, 'PM'); // Force uppercase AM/PM
        return `${dateStr}, ${timeStr}`;
    };

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center gap-4">
                    {/* Left Side - Icon, Title with Badge - 25% */}
                    <div className="flex items-center gap-3" style={{ flex: '0 0 25%' }}>
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Star className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-semibold text-gray-900">Reviews Management</h1>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brandColor text-white">
                                    {pagination.totalItems.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500">Manage and respond to school reviews</p>
                        </div>
                    </div>

                    {/* Right Side - Search and Date Filters - 75% */}
                    <div className="flex items-center gap-3 justify-end flex-wrap" style={{ flex: '0 0 75%' }}>
                        {/* Search */}
                        <div className="relative" style={{ width: '250px' }}>
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search reviews..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Date Filters */}
                        <div className="flex items-center gap-2">
                            <Calendar size={18} className="text-gray-500" />
                            <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleApplyFilters}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>

                        {/* Claimed Toggle */}
                        <select
                            value={claimedFilter}
                            onChange={(e) => setClaimedFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Claimed</option>
                            <option value="1">Claimed</option>
                            <option value="0">Not Claimed</option>
                        </select>

                        {/* Client Toggle */}
                        <select
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Client</option>
                            <option value="1">Client</option>
                            <option value="0">Not Client</option>
                        </select>

                        {/* Export */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                            leftIcon={<Download size={16} />}
                        >
                            {isExporting ? 'Exporting...' : 'Export'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto px-4 mt-4">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full table-auto border-collapse">
                        <thead className="bg-brandColor text-white sticky top-0 z-10">
                            <tr>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('school_name')}
                                >
                                    School {sortColumn === 'school_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('r_name')}
                                >
                                    Name {sortColumn === 'r_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('r_phone')}
                                >
                                    Phone {sortColumn === 'r_phone' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('city_name')}
                                >
                                    City {sortColumn === 'city_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('area_name')}
                                >
                                    Area {sortColumn === 'area_name' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('review_date')}
                                >
                                    Review Date {sortColumn === 'review_date' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('rating')}
                                >
                                    Rating {sortColumn === 'rating' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                                    Review
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('claimed')}
                                >
                                    Claimed {sortColumn === 'claimed' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th
                                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer hover:bg-opacity-90 transition-colors whitespace-nowrap"
                                    onClick={() => handleSort('is_client')}
                                >
                                    Client {sortColumn === 'is_client' && (sortDirection === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                                        Loading reviews...
                                    </td>
                                </tr>
                            ) : reviews.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                                        No reviews found
                                    </td>
                                </tr>
                            ) : (
                                reviews.map((review, index) => (
                                    <tr
                                        key={review.reviewId}
                                        className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                        onClick={() => handleReviewClick(review)}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-800 font-medium">
                                            {review.school_name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {review.r_name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {review.r_phone || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {review.city_name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {review.area_name || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">
                                            {formatDate(review.review_date)}
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            <div className="flex items-center gap-1">
                                                <Star size={14} className="text-yellow-500 fill-yellow-500" />
                                                <span className="font-semibold">{review.rating}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-600">
                                            {truncateText(review.review_text, 150)}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {review.claimed === 1 ? (
                                                <CheckCircle size={18} className="text-green-600 inline" />
                                            ) : (
                                                <XCircle size={18} className="text-gray-400 inline" />
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-center">
                                            {review.is_client === 1 ? (
                                                <CheckCircle size={18} className="text-green-600 inline" />
                                            ) : (
                                                <XCircle size={18} className="text-gray-400 inline" />
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                    Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                    {pagination.totalItems} results
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronsLeft size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronRight size={16} />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        <ChevronsRight size={16} />
                    </Button>
                </div>
            </div>

            {/* Review Detail Modal */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Review Details"
                subtitle={selectedReview ? `Review by ${selectedReview.r_name}` : ''}
                icon={<div className="text-brandColor"><Star size={24} /></div>}
                width="600px"
            >
                {selectedReview && (
                    <>
                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* School Info */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <h3 className="font-semibold text-gray-900">{selectedReview.school_name}</h3>
                                <div className="text-sm text-gray-600">
                                    {selectedReview.area_name}, {selectedReview.city_name}
                                </div>
                            </div>

                            {/* Reviewer Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Reviewer Name</label>
                                    <p className="text-sm font-medium text-gray-900">{selectedReview.r_name}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Phone</label>
                                    <p className="text-sm font-medium text-gray-900">{selectedReview.r_phone || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Email</label>
                                    <p className="text-sm font-medium text-gray-900">{selectedReview.r_email || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Review Date</label>
                                    <p className="text-sm font-medium text-gray-900">{formatDate(selectedReview.review_date)}</p>
                                </div>
                            </div>

                            {/* Rating */}
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Rating</label>
                                <div className="flex items-center gap-1 mt-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star
                                            key={i}
                                            size={20}
                                            className={i < selectedReview.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                                        />
                                    ))}
                                    <span className="ml-2 text-sm font-semibold">{selectedReview.rating}/5</span>
                                </div>
                            </div>

                            {/* Review Text */}
                            <div>
                                <label className="text-xs text-gray-500 uppercase">Review</label>
                                <div className="mt-2 p-4 bg-gray-50 rounded-lg text-sm text-gray-900 whitespace-pre-wrap">
                                    {selectedReview.review_text || 'No review text provided'}
                                </div>
                            </div>

                            {/* Relationship */}
                            {selectedReview.relationship_with_school && (
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Relationship with School</label>
                                    <p className="text-sm font-medium text-gray-900">{selectedReview.relationship_with_school}</p>
                                </div>
                            )}

                            {/* Reply Section */}
                            <div className="border-t pt-6">
                                <label className="text-sm font-semibold text-gray-900 mb-2 block">Your Reply</label>
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply here..."
                                    rows={6}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        {/* Fixed Footer with Buttons */}
                        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="outline"
                                    onClick={handleCloseModal}
                                    disabled={isSubmittingReply}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSubmitReply}
                                    disabled={isSubmittingReply || !replyText.trim()}
                                >
                                    {isSubmittingReply ? 'Submitting...' : 'Submit Reply'}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </SettingsSlideModal>
        </div>
    );
};

export default ReviewsAdmin;
